/**
 * StatCard - Reusable metric display card component
 * Used across Dashboard, FCA Admin, IWMS Admin, etc.
 */
import React from 'react';
import { Card } from './ui/card';
import { ChevronRight } from 'lucide-react';

/**
 * StatCard - A clickable card showing a single metric
 * 
 * @param {string} title - The label for the stat (e.g., "Total Clients")
 * @param {string|number} value - The metric value to display
 * @param {React.Component} icon - Lucide icon component
 * @param {string} iconColor - Tailwind color class (e.g., "text-teal-600")
 * @param {function} onClick - Click handler for navigation
 * @param {string} actionText - Text for the action link (e.g., "View all")
 * @param {string} variant - "default" | "gradient" | "highlight"
 * @param {string} gradientFrom - For gradient variant (e.g., "from-teal-500")
 * @param {string} gradientTo - For gradient variant (e.g., "to-teal-600")
 * @param {string} highlightColor - For highlight variant (e.g., "amber" | "red" | "blue")
 * @param {boolean} isAlert - If true, applies alert styling when value > 0
 * @param {string} testId - data-testid for testing
 * @param {string} size - "default" | "small" | "large"
 */
export const StatCard = ({
  title,
  value,
  icon: Icon,
  iconColor = 'text-slate-500',
  onClick,
  actionText,
  variant = 'default',
  gradientFrom,
  gradientTo,
  highlightColor,
  isAlert = false,
  testId,
  size = 'default',
  badge,
  className = '',
}) => {
  const isClickable = !!onClick;
  
  // Determine if alert state is active
  const alertActive = isAlert && Number(value) > 0;
  
  // Size classes
  const sizeClasses = {
    small: {
      padding: 'p-4',
      title: 'text-xs',
      value: 'text-2xl',
      icon: 24,
    },
    default: {
      padding: 'p-6',
      title: 'text-xs',
      value: 'text-3xl',
      icon: 32,
    },
    large: {
      padding: 'p-8',
      title: 'text-sm',
      value: 'text-4xl',
      icon: 40,
    },
  };
  
  const currentSize = sizeClasses[size] || sizeClasses.default;
  
  // Build card classes based on variant
  const getCardClasses = () => {
    const base = `${currentSize.padding} transition-all ${isClickable ? 'cursor-pointer' : ''}`;
    
    switch (variant) {
      case 'gradient':
        return `${base} bg-gradient-to-br ${gradientFrom || 'from-teal-500'} ${gradientTo || 'to-teal-600'} text-white hover:shadow-lg`;
      
      case 'highlight':
        const colors = {
          amber: alertActive 
            ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 hover:border-amber-400' 
            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
          red: alertActive 
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 hover:border-red-400' 
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
          blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:border-blue-400',
          green: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 hover:border-emerald-400',
        };
        return `${base} border ${colors[highlightColor] || colors.amber}`;
      
      default:
        const hoverColor = iconColor?.replace('text-', 'hover:border-')?.replace('-600', '-400')?.replace('-500', '-400') || 'hover:border-slate-400';
        return `${base} bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 ${isClickable ? `${hoverColor} hover:shadow-md` : ''}`;
    }
  };
  
  // Get value text color
  const getValueColor = () => {
    if (variant === 'gradient') return 'text-white';
    if (variant === 'highlight') {
      const colors = {
        amber: alertActive ? 'text-amber-700 dark:text-amber-300' : 'text-amber-700 dark:text-amber-300',
        red: alertActive ? 'text-red-700 dark:text-red-300' : 'text-red-700 dark:text-red-300',
        blue: 'text-blue-700 dark:text-blue-300',
        green: 'text-emerald-700 dark:text-emerald-300',
      };
      return colors[highlightColor] || 'text-slate-900 dark:text-white';
    }
    if (isAlert && alertActive) {
      return iconColor;
    }
    return 'text-slate-900 dark:text-white';
  };
  
  // Get title text color
  const getTitleColor = () => {
    if (variant === 'gradient') return 'opacity-80';
    if (variant === 'highlight') {
      const colors = {
        amber: 'text-amber-600 dark:text-amber-400',
        red: 'text-red-600 dark:text-red-400',
        blue: 'text-blue-600 dark:text-blue-400',
        green: 'text-emerald-600 dark:text-emerald-400',
      };
      return colors[highlightColor] || 'text-slate-500 dark:text-slate-400';
    }
    return 'text-slate-500 dark:text-slate-400';
  };
  
  const cardClasses = getCardClasses();
  const titleColorClass = getTitleColor();
  const valueColorClass = getValueColor();
  
  const cardContent = (
    <>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className={`${currentSize.title} font-semibold uppercase tracking-widest ${titleColorClass} mb-1`}>
            {title}
          </p>
          <p className={`${currentSize.value} font-black ${valueColorClass}`}>
            {value}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {badge}
          {Icon && (
            <Icon 
              className={variant === 'gradient' ? 'text-white opacity-80' : iconColor} 
              size={currentSize.icon} 
            />
          )}
        </div>
      </div>
      {actionText && (
        <div className={`mt-2 text-xs ${variant === 'gradient' ? 'text-white opacity-70' : iconColor} flex items-center gap-1`}>
          {actionText} <ChevronRight size={14} />
        </div>
      )}
    </>
  );
  
  if (isClickable) {
    return (
      <Card 
        className={`${cardClasses} ${className}`}
        onClick={onClick}
        data-testid={testId}
      >
        {cardContent}
      </Card>
    );
  }
  
  return (
    <Card className={`${cardClasses} ${className}`} data-testid={testId}>
      {cardContent}
    </Card>
  );
};

/**
 * StatCardGrid - Container for organizing StatCards in a responsive grid
 */
export const StatCardGrid = ({ 
  children, 
  cols = 4, 
  className = '' 
}) => {
  const gridCols = {
    2: 'grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4',
    6: 'grid-cols-2 md:grid-cols-4 lg:grid-cols-6',
  };
  
  return (
    <div className={`grid ${gridCols[cols] || gridCols[4]} gap-4 ${className}`}>
      {children}
    </div>
  );
};

export default StatCard;
